import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../api/client';

/**
 * Универсальный асинхронный селект с поиском по Flexbase
 * Пример:
 * <AsyncSearchSelect
 *   name="client_id"
 *   label="Клиент"
 *   value={formData.client_id}
 *   onChange={handleChange}
 *   searchDoc="clients"
 *   searchField="name"
 *   fallbackOptions={clients}
 *   getOptionId={(o) => o.id?.toString?.()}
 *   getOptionLabel={(o) => o.name}
 * />
 */
const AsyncSearchSelect = ({
  name,
  label,
  value,
  onChange,
  searchDoc,
  searchField,
  fallbackOptions = [],
  getOptionId = (o) => o.id?.toString?.(),
  getOptionLabel = (o) => o.name || o.label || '',
  disabled = false,
  required = false,
  sx,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const selectedOption = useMemo(() => {
    if (value == null || value === '') return null;
    const idStr = value.toString();
    return options.find((o) => getOptionId(o) === idStr)
      || fallbackOptions.find((o) => getOptionId(o) === idStr)
      || null;
  }, [value, options, fallbackOptions, getOptionId]);

  useEffect(() => {
    let ignore = false;
    let timeoutId;

    const doSearch = async () => {
      if (!searchDoc || !searchField) return;
      setLoading(true);
      try {
        const params = { page: 1, per_page: 20 };
        if (query && query.trim()) {
          // Универсальный OR-поиск на бэкенде с поддержкой регистра
          params.q = query.trim();
          params.fields = Array.isArray(searchField) ? searchField.join(',') : String(searchField);
        }
        const resp = await apiClient.get(`/documents/${encodeURIComponent(searchDoc)}/search`, { params });
        if (!ignore) {
          const list = Array.isArray(resp.data) ? resp.data : [];
          // Нормализуем id для совместимости с fallbackOptions (id vs _id)
          const normalized = list.map((o) => ({ ...o, id: o.id ?? o._id }));
          setOptions(normalized);
        }
      } catch (e) {
        if (!ignore) setOptions([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    if (!open) {
      // Когда меню закрыто — не грузим
      return () => { };
    }

    // При пустом запросе: подгружаем 1-ю страницу из Flexbase и объединяем с fallback
    if (!query) {
      (async () => {
        try {
          setLoading(true);
          const resp = await apiClient.get(`/documents/${encodeURIComponent(searchDoc)}/search`, { params: { page: 1, per_page: 20 } });
          const list = Array.isArray(resp.data) ? resp.data : [];
          const normalizedFetched = list.map((o) => ({ ...o, id: o.id ?? o._id }));
          const normalizedFallback = (fallbackOptions || []).map((o) => ({ ...o, id: o.id ?? o._id }));
          const byId = new Map();
          [...normalizedFallback, ...normalizedFetched].forEach((it) => { if (it && (it.id != null)) byId.set(String(it.id), it); });
          if (!ignore) setOptions(Array.from(byId.values()));
        } catch (e) {
          if (!ignore) setOptions(fallbackOptions || []);
        } finally {
          if (!ignore) setLoading(false);
        }
      })();
      return () => { };
    }

    timeoutId = setTimeout(doSearch, 300);
    return () => { ignore = true; clearTimeout(timeoutId); };
  }, [open, query, searchDoc, searchField, fallbackOptions]);

  const handleChangeInternal = (event, newValue) => {
    const id = newValue ? getOptionId(newValue) : '';
    if (onChange) {
      onChange({ target: { name, value: id } });
    }
  };

  return (
    <Autocomplete
      sx={sx}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      value={selectedOption}
      onChange={handleChangeInternal}
      onInputChange={(e, val) => setQuery(val)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(opt, val) => getOptionId(opt) === getOptionId(val)}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default AsyncSearchSelect;


